// @flow
import React, { Component } from "react";
import classNames from "classnames";
import AriaModal from "react-aria-modal";
import Close from "elevate-ui-icons/Close";
import withStyles from "elevate-ui/withStyles";
import first from "lodash/first";
import Tag from "../common/Tag";

import Slideshow from "./Slideshow";
import { withListingContext } from "./ListingContext";
import { withGlobalContext } from "../common/GlobalContext";

import type { CompliantListingType, $OpenHouses } from "flow/types";

import type { $ListingPhotoViewModel } from "@elm-street-technology/listing/flow/view-models";

const defaultPhotoWidth = 280;
const defaultPhotoHeight = 180;
const gridGap = 4;
const featuredPhotoWidth = defaultPhotoWidth * 2 + gridGap;
const featuredPhotoHeight = defaultPhotoHeight * 2 + gridGap;

type $Props = {
  classes: Object,
  className: string,
  compliance: Object,
  listing: CompliantListingType,
  openHouses: $OpenHouses,
  CDN_IMAGE_SERVER_URL: string,
  theme: Object,
  trackLeadAction: Function,
  isV1: Boolean,
};

type $State = {
  selectedPhoto: $ListingPhotoViewModel | null,
  selectedPhotoIndex: number | null,
  showModal: boolean,
};

class Gallery extends Component<$Props, $State> {
  constructor(props) {
    super(props);

    this.state = {
      selectedPhoto: null,
      selectedPhotoIndex: null,
      showModal: false,
    };
  }

  handleOpenModal = (selectedPhoto, selectedPhotoIndex) => {
    this.setState({
      selectedPhoto,
      selectedPhotoIndex,
      showModal: true,
    });
  };

  handleCloseModal = () => {
    const { listing } = this.props;
    this.setState({
      selectedPhoto: {
        description: "",
        hash: "",
        id: "",
        listingId: listing.id,
        path: "",
        position: 0,
      },
      selectedPhotoIndex: null,
      showModal: false,
    });
  };

  render() {
    const {
      CDN_IMAGE_SERVER_URL,
      classes,
      className,
      compliance,
      listing,
      trackLeadAction,
      openHouses,
      theme,
      isV1,
    } = this.props;
    const { selectedPhoto, selectedPhotoIndex, showModal } = this.state;
    const watermarkQuery =
      compliance && compliance.photoWatermark
        ? `&mark=${compliance.photoWatermark}&markscale=${compliance.markScale}`
        : "";
    const openHouse = openHouses && openHouses.length && first(openHouses);

    return (
      <div className={classNames(classes.root, className)}>
        {listing.photos &&
          listing.photos.map((photo, i) => {
            let photoPath = photo.path;

            const queryString =
              i === 0
                ? `&auto=format&fit=crop&w=${featuredPhotoWidth}&h=${featuredPhotoHeight}&or=0${watermarkQuery}`
                : `&auto=format&fit=crop&w=${defaultPhotoWidth}&h=${defaultPhotoHeight}&or=0${watermarkQuery}`;

            if (isV1) {
              const basePath = `${CDN_IMAGE_SERVER_URL}/${photo.path}?`;
              photoPath = photo.hash
                ? `${basePath}hash=${photo.hash}`
                : basePath;
            }

            return (
              <a
                key={photo.id}
                className={classNames(
                  classes.photo,
                  i === 0 && classes.photoFeatured
                )}
                href={photoPath}
                onClick={(e) => {
                  e.preventDefault(); // if JS is enabled, we'll block the link and show the modal
                  trackLeadAction({
                    action: "viewed-full-sized-images",
                    listingId: listing.id,
                  });
                  const selectedPhotoIndex = i + 1;
                  this.handleOpenModal(photo, selectedPhotoIndex);
                }}
              >
                {i === 0 && openHouse && openHouse.openHouseDescription ? (
                  <span className={classes.tag}>
                    <Tag
                      status="hasOpenHouse"
                      title={`${openHouse.title} - ${
                        openHouse.openHouseDescription
                      }`}
                      className="ref__openHouseTag"
                    />
                  </span>
                ) : null}
                <img
                  className={classes.img}
                  srcSet={`
                  ${photoPath}${queryString}&dpr=1 1x,
                  ${photoPath}${queryString}&dpr=2 2x,
                  ${photoPath}${queryString}&dpr=3 3x,
                `}
                  sizes={
                    i === 0
                      ? `${featuredPhotoWidth}px`
                      : `${defaultPhotoWidth}px`
                  }
                  src={`${photoPath}${queryString}`}
                  alt={`Real estate listing preview #${i + 1}`}
                />
              </a>
            );
          })}
        <AriaModal
          mounted={showModal}
          onExit={this.handleCloseModal}
          titleText={`Photo slideshow for ${listing.fullAddress}`}
          dialogStyle={{ width: "100%", height: "100%" }}
          underlayStyle={{
            backgroundColor: "rgba(0,0,0,0.9)",
            padding: theme.spacing.unit * 0.75,
            zIndex: theme.zIndex.modal,
          }}
          initialFocus={`#galleryBtn-${
            this.state.selectedPhotoIndex ? this.state.selectedPhotoIndex : 0
          }`}
          scrollDisabled
        >
          <button
            className={classes.closeButton}
            onClick={this.handleCloseModal}
          >
            <Close width={32} height={32} />
          </button>
          <Slideshow
            photos={listing.photos}
            selectedPhoto={selectedPhoto}
            selectedPhotoIndex={selectedPhotoIndex}
            isV1={isV1}
          />
        </AriaModal>
      </div>
    );
  }
}

export default withStyles((theme) => ({
  photo: {
    display: "flex",
    ".ie-11 &": {
      display: "inline-block",
    },

    "&:focus": {
      outline: "2px solid #fff", // override blue focus ring with white solid ring
    },
  },
  img: {
    width: "100%",
    height: "100%",
  },
  tag: {
    position: "absolute",
    top: "8px",
    left: "8px",
  },
  closeButton: {
    position: "absolute",
    top: "8px",
    right: "8px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.colors.white,
    padding: theme.spacing.unit * 0.5,
    zIndex: theme.zIndex["modalControl"],
    background: "rgba(0,0,0,0.5)",
    borderRadius: theme.globalBorderRadius,

    "&:focus": {
      outline: "2px solid #fff !important", // override blue focus ring with white solid ring
    },
  },
  [theme.breakpoints(900)]: {
    root: {
      gridTemplateColumns: `repeat(auto-fill, ${defaultPhotoWidth}px)`,
      gridTemplateRows: `repeat(2, ${defaultPhotoHeight}px)`,
      position: "relative",
      marginBottom: theme.spacing.unit * 1.5,
      display: "grid",
      gridGap: `${gridGap}px`,
      gridAutoFlow: "column",
      maxHeight: `${defaultPhotoHeight * 2 + gridGap + 4}px`, // 4px is derived from the 2px padding
      overflow: "hidden",
      overflowX: "scroll",
      WebkitOverflowScrolling: "touch",
      ".ie-11 &": {
        display: "flex",
        flexFlow: "row nowrap",
        alignItems: "center",
      },
    },
    photoFeatured: {
      gridColumn: "1 / span 2",
      gridRow: "1 / span 2",
    },
    img: {
      width: "inherit",
    },
  },
}))(withGlobalContext(withListingContext(Gallery)));
